import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, ChevronDown } from 'lucide-react';
import { businessInfo } from '../mockData';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isPricesOpen, setIsPricesOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Hjem' },
    { path: '/tjenester', label: 'Tjenester' },
    { path: '/om-oss', label: 'Om oss' },
    { path: '/kontakt', label: 'Kontakt' }
  ];

  const priceLinks = [
    { path: '/priser/mobil', label: 'Mobilreparasjon' },
    { path: '/priser/pc', label: 'PC & Laptop' }
  ];

  const isActive = (path) => location.pathname === path;
  const isPricesActive = location.pathname.startsWith('/priser');

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md shadow-sm">
      {/* Top bar with phone */}
      <div className="bg-gradient-to-r from-sky-500 to-cyan-500 text-white py-2">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="text-sm font-medium">
            Velkommen til {businessInfo.name}
          </div>
          <a 
            href={`tel:${businessInfo.phone}`}
            className="flex items-center gap-2 text-sm font-semibold hover:scale-105 transition-transform"
          >
            <Phone size={16} />
            <span>{businessInfo.phone}</span>
          </a>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-sky-500 to-cyan-500 rounded-lg blur opacity-25 group-hover:opacity-40 transition-opacity"></div>
              <div className="relative bg-gradient-to-r from-sky-500 to-cyan-500 text-white px-4 py-2 rounded-lg font-bold text-xl">
                DrMobil
              </div>
            </div>
            <span className="text-gray-700 font-semibold text-lg">Strommen</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-medium transition-colors relative group ${
                  isActive(link.path)
                    ? 'text-sky-600'
                    : 'text-gray-700 hover:text-sky-600'
                }`}
              >
                {link.label}
                <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-sky-600 transform origin-left transition-transform ${
                  isActive(link.path) ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                }`}></span>
              </Link>
            ))}
            
            {/* Prices Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setIsPricesOpen(true)}
              onMouseLeave={() => setIsPricesOpen(false)}
            >
              <button
                className={`font-medium transition-colors relative group flex items-center gap-1 ${
                  isPricesActive
                    ? 'text-sky-600'
                    : 'text-gray-700 hover:text-sky-600'
                }`}
              >
                Priser
                <ChevronDown size={16} className={`transition-transform ${isPricesOpen ? 'rotate-180' : ''}`} />
                <span className={`absolute bottom-0 left-0 w-full h-0.5 bg-sky-600 transform origin-left transition-transform ${
                  isPricesActive ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                }`}></span>
              </button>
              
              {/* Dropdown Menu */}
              {isPricesOpen && (
                <div className="absolute top-full left-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-100 py-2 min-w-[200px] animate-in slide-in-from-top">
                  {priceLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className="block px-4 py-2 text-gray-700 hover:bg-sky-50 hover:text-sky-600 transition-colors"
                    >
                      {link.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t pt-4 animate-in slide-in-from-top">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block py-3 px-4 rounded-lg font-medium transition-colors ${
                  isActive(link.path)
                    ? 'bg-sky-50 text-sky-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Mobile Prices Section */}
            <div className="mt-2">
              <div className="px-4 py-2 text-sm font-semibold text-gray-500">Priser</div>
              {priceLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block py-3 px-4 ml-4 rounded-lg font-medium transition-colors ${
                    isActive(link.path)
                      ? 'bg-sky-50 text-sky-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
